import { useEffect, useState } from 'react';

import { copy } from '../../core/copy';
import { GlasanjeStrip, IzazovModal } from './Dvoboj';
import type { Peer, PeerWork } from '../../core/posada';
import { nextStep, nodeStatusOf } from '../../core/rules';
import { Btn, HeroPortrait, Modal, Win } from '../components';
import {
  contentOf,
  loadPosadaView,
  openNode,
  openPeer,
  playerOf,
  posadaOf,
  savePosadaMe,
  toast,
  toggleHidden,
  useStore,
} from '../store';
import { RadRed } from './Komentari';

/**
 * POSADA — the people.
 *
 * The last screen to appear and the only one that is about somebody else. There is no score on it,
 * no ranking, and no count of anything a person has done for the crew: the research this product
 * is built on says belonging is worth more than any of that (g = 1,776 against 0,277 for
 * competence), and a leaderboard of helpfulness would convert the one into the other overnight.
 *
 * Two lists. Who is around, and whose work is waiting. That is the whole screen.
 */

/** How many waiting works are drawn before the rest become one quiet line. */
const WORKS_SHOWN = 5;
export function Posada() {
  const view = useStore((s) => s.posadaView);
  const focus = useStore((s) => s.focusPeer);
  const [open, setOpen] = useState<Peer | null>(null);

  /**
   * Kept fresh while this screen is open, which also covers the failure case: `loadPosadaView`
   * swallows a bad request on purpose (the crew is never worth an error screen), so without a
   * retry a dropped fetch would leave this screen on its loading ellipsis for good.
   */
  useEffect(() => {
    void loadPosadaView();
    // Comfortably past the store's own 20-second freshness window, so a tick is never wasted
    // landing exactly on the boundary and being skipped.
    const timer = window.setInterval(() => void loadPosadaView(), 25_000);
    return () => window.clearInterval(timer);
  }, []);

  // Arriving from „vrati komentar" or from walking into somebody: open them straight away, once
  // the crew has actually loaded.
  useEffect(() => {
    if (!focus || !view) return;
    const peer = view.peers.find((p) => p.id === focus);
    if (peer) setOpen(peer);
    else if (view.works.some((w) => w.memberId === focus)) {
      // Off the visible map — they were commenting from further ahead — but their work is still
      // here to answer. Build the barest peer so the card can open on it.
      setOpen({
        id: focus,
        name: view.works.find((w) => w.memberId === focus)?.name ?? '',
        look: 0,
        souvenirs: [],
        nodeId: null,
        islandId: null,
      });
    }
    openPeer(null);
  }, [focus, view]);

  if (!view) {
    return (
      <div className="pad">
        <p className="muted">…</p>
      </div>
    );
  }

  if (!view.visible) {
    return (
      <div className="pad">
        <Win title={copy.posada.title}>
          <p className="leads">{copy.posada.hidden}</p>
          <JaUPosadi view={view} />
        </Win>
      </div>
    );
  }

  return (
    <div className="pad">
      <Win title={copy.posada.title}>
        <p className="leads" style={{ marginBottom: 0 }}>
          {copy.posada.lead}
        </p>
      </Win>

      {/*
        Duels waiting for a vote. The only place in the product that offers one — there is no badge,
        no count and no notification, because GDD §2① says nothing may pull anybody anywhere.
      */}
      <GlasanjeStrip duels={view.dvoboji ?? []} />

      <Win title={copy.posada.worksTitle}>
        <p className="leads">{copy.posada.worksLead}</p>
        {view.works.length === 0 ? (
          <p className="muted tiny">{copy.posada.nothingWaiting}</p>
        ) : (
          <>
            <div className="nodelist">
              {/*
                A wall of forty cards is the overwhelm this product exists to cure, pointed at a
                new target. The list is cut to a handful in the honest work order — and it says so
                rather than truncating quietly, because a queue that looks finished when it is not
                is worse than a long one.
              */}
              {view.works.slice(0, WORKS_SHOWN).map((work) => (
                <RadRed key={`${work.memberId}:${work.nodeId}`} work={work} />
              ))}
            </div>
            {view.works.length > WORKS_SHOWN ? (
              <p className="muted tiny" style={{ marginTop: 10 }}>
                {copy.posada.moreWaiting(view.works.length - WORKS_SHOWN)}
              </p>
            ) : null}
          </>
        )}
      </Win>

      <Win title={copy.posada.whoTitle}>
        {view.peers.length === 0 ? (
          <p className="muted tiny">{copy.posada.nobody}</p>
        ) : (
          <div className="nodelist">
            {view.peers.map((peer) => (
              <PeerRow key={peer.id} peer={peer} onOpen={() => setOpen(peer)} />
            ))}
          </div>
        )}
      </Win>

      <Win title={copy.posada.meTitle}>
        <JaUPosadi view={view} />
      </Win>

      {open ? (
        <PeerCard
          peer={open}
          works={view.works.filter((work) => work.memberId === open.id)}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ one person */

function PeerRow({ peer, onOpen }: { peer: Peer; onOpen: () => void }) {
  const content = useStore(contentOf);
  const island = content.islands.find((i) => i.id === peer.islandId);
  const node = island?.nodes.find((n) => n.id === peer.nodeId);
  return (
    <button className="noderow" onClick={onOpen}>
      <span className="glyph">
        <HeroPortrait look={peer.look} scale={2} souvenirs={peer.souvenirs} />
      </span>
      <span className="info">
        <span className="nm">{peer.name}</span>
        <span className="meta">
          {island ? copy.posada.onIsland(island.name) : ''}
          {node ? ` · ${copy.posada.doing(node.title)}` : ''}
        </span>
      </span>
    </button>
  );
}

/**
 * One member, their works, and the button that answers them. Reached from the list here and from
 * walking into them on the map — the same card either way, so the map does not need its own idea
 * of what a person is.
 */
export function PeerCard({
  peer,
  works,
  onClose,
}: {
  peer: Peer;
  works: PeerWork[];
  onClose: () => void;
}) {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const posada = useStore(posadaOf);
  const hidden = useStore((s) => s.hidden.includes(peer.id));
  const [challenging, setChallenging] = useState(false);
  const island = content.islands.find((i) => i.id === peer.islandId);
  const node = island?.nodes.find((n) => n.id === peer.nodeId);

  /**
   * The two of you are standing at the same task.
   *
   * The most useful thing synchrony can produce in this curriculum, and it costs one comparison:
   * GDD §1 puts most of the community on one plateau, so „stuck on the same thing at the same
   * moment" is both common and completely invisible. Said only when it is true, only on the card
   * of somebody you went and opened, and nowhere else in the product.
   */
  const step = nextStep(content, player, null);
  const myNode = step.kind === 'rad' || step.kind === 'popravka' ? step.view.node.id : null;
  const together = peer.nodeId !== null && peer.nodeId === myNode;

  /**
   * They commented on your work, and the revision is still unmade.
   *
   * This is the whole reason walking up to somebody matters. The one number GDD §12 says is worth
   * measuring is the share of members who post a revised version, and the moment to ask is when the
   * person who wrote the comment is standing in front of you. Passive — true when it is true, said
   * when you open the card, and never a notification (GDD §2① — nothing may pull anyone to the map).
   */
  const owed = posada.inbox.find((item) => {
    if (item.fromId !== peer.id || item.missingAt) return false;
    const on = content.islands.flatMap((i) => i.nodes).find((n) => n.id === item.nodeId);
    // A node this path no longer contains is not a revision anybody can be pointed at.
    return on ? nodeStatusOf(on, player.progress[item.nodeId]) === 'feedback' : false;
  });

  return (
    <Modal title={peer.name} onClose={onClose}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <HeroPortrait look={peer.look} scale={3} souvenirs={peer.souvenirs} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="title" style={{ fontSize: 17 }}>
            {peer.name}
          </h2>
          <p className="tiny muted" style={{ marginTop: 2 }}>
            {island ? copy.posada.onIsland(island.name) : ''}
            {node ? ` · ${copy.posada.doing(node.title)}` : peer.islandId ? ` · ${copy.posada.idle}` : ''}
          </p>
          {together ? <p className="tiny" style={{ marginTop: 2 }}>{copy.uzivo.sameNode}</p> : null}
        </div>
      </div>

      {owed ? (
        <div className="card-head" style={{ marginBottom: 12 }}>
          <span className="chip gold">{copy.uzivo.owedRevision}</span>
          <Btn
            size="small"
            onClick={() => {
              onClose();
              openNode(owed.nodeId);
            }}
          >
            {copy.uzivo.openRevision}
          </Btn>
        </div>
      ) : null}

      {works.length === 0 ? (
        <p className="muted tiny">{copy.posada.nothingWaiting}</p>
      ) : (
        <div className="nodelist">
          {works.map((work) => (
            <RadRed key={work.nodeId} work={work} onDone={onClose} />
          ))}
        </div>
      )}

      {/*
        The duel, offered from the card you already opened by walking up to somebody. Not offered at
        all to a person this device has hidden — „Sakrij" means they do not reach you, and a
        challenge is reaching somebody.
      */}
      {!hidden ? (
        <div className="btn-row" style={{ marginTop: 16 }}>
          <Btn size="small" onClick={() => setChallenging(true)}>
            {copy.dvoboj.challenge}
          </Btn>
        </div>
      ) : null}
      {challenging ? (
        <IzazovModal memberId={peer.id} islandId={peer.islandId} onClose={() => setChallenging(false)} />
      ) : null}

      {/* Last, small, and without a word of explanation anywhere else in the product. */}
      <div className="btn-row" style={{ marginTop: 16 }}>
        <Btn
          variant="ghost"
          size="small"
          onClick={() => {
            toggleHidden(peer.id);
            if (!hidden) toast(copy.uzivo.hidden, copy.uzivo.hiddenBody);
          }}
        >
          {hidden ? copy.uzivo.unhide : copy.uzivo.hide}
        </Btn>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ me */

/**
 * The name other people see, and the one switch that takes you out of the crew.
 *
 * The name is asked for here rather than at onboarding because day one has two screens and one
 * card, and „kako te zovu drugi" is a question about other people. It becomes a real question at
 * the exact moment this screen appears, and not a minute earlier.
 */
function JaUPosadi({ view }: { view: { name: string; visible: boolean } }) {
  const posada = useStore(posadaOf);
  const [name, setName] = useState(view.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (patch: { name?: string; visible?: boolean }) => {
    setBusy(true);
    setError(null);
    const result = await savePosadaMe(patch);
    setBusy(false);
    if (result.ok) {
      toast(copy.posada.saved);
      void loadPosadaView(true);
    } else setError(result.message);
  };

  return (
    <>
      <label className="lbl" htmlFor="posada-ime">
        {copy.posada.nameLabel}
      </label>
      <input
        id="posada-ime"
        className="field"
        value={name}
        maxLength={40}
        placeholder={copy.posada.namePlaceholder}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => {
          if (name.trim() !== view.name) void save({ name: name.trim() });
        }}
        disabled={busy}
      />
      <p className="tiny muted" style={{ marginTop: 6 }}>
        {copy.posada.nameHint}
      </p>

      <p className="tiny muted" style={{ marginTop: 14 }}>
        {copy.posada.hideHint}
      </p>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <Btn variant="ghost" size="small" disabled={busy} onClick={() => void save({ visible: !posada.visible })}>
          {posada.visible ? copy.posada.hide : copy.posada.show}
        </Btn>
      </div>
      {error ? (
        <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>
          {error}
        </p>
      ) : null}
    </>
  );
}

